import { Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useIntl } from "react-intl";
import CardNew from "components/molecules/Card/CardNew";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Badge from "components/atoms/Badge/Badge";
import { renderAddressV2InfoForHeaders } from "helpers/addressV2Helpers";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import PropTypes from "prop-types";
import { QuickRequestStatusColors } from "constants";
import { Link } from "react-router-dom";
import { ReactComponent as CalculatorIcon } from "assets/images/icons/CalculatorBlue.svg";
import { DateTimeFormat, formatDate, parseDate } from "helpers/dateTimeHelper";

import "../ShipmentQuotes.scss";

function QuickQuoteInfo({ data, loadingData }) {
  const {
    pickup_address_name,
    pickup_country_code,
    pickup_state_code,
    pickup_city,
    pickup_zipcode,
    pickup_date,
    pickup_time,
    pickup_end_date,
    pickup_end_time,
    stop1_address_name,
    stop1_country_code,
    stop1_state_code,
    stop1_city,
    stop1_zipcode,
    stop1_date,
    stop1_time,
    stop1_end_date,
    stop1_end_time,
    stop1_datetime,
    address_v2,
  } = data.schedule || {};

  const { renderPickupAddress, renderDeliveryAddress } = renderAddressV2InfoForHeaders({ data });

  const {
    quick_request_creation_date,
    quick_request_status,
    quick_request_created_by_name,
    quick_request_created_by_email,
  } = data.organization || {};

  const { related_request_id, related_request_status } = data.related_request || {};

  const shipmentRedirectURL = ["Booked", "In Transit", "Delayed", "Delivered", "Incomplete"].includes(related_request_status)
    ? `/shipment/${related_request_id}/tracking`
    : `/shipment/${related_request_id}`;

  const intl = useIntl();

  const header = (title) => (
    <p
      className="font-b5 text-secondary"
      style={{
        padding: 0,
        marginBottom: "7px",
      }}
    >
      {title}
    </p>
  );

  if (loadingData)
    return (
      <Row lg={12} className="ml-0 mr-0" style={{ marginBottom: "16px" }}>
        <CardNew className="w-100 border-radius-top-0">
          <LoadingSkeleton rows={1} />
        </CardNew>
      </Row>
    );

  return (
    <Row lg={12} className="ml-0 mr-0" style={{ marginBottom: "16px" }}>
      <CardNew className="w-100 border-radius-top-0">
        <CardNew.Body>
          <Row>
            <Col className="col-12 mb-3" md>
              {header(intl.formatMessage({ id: "general.createdBy", defaultMessage: "Created By" }))}
              <div
                className="cursor-pointer"
                style={{ transform: "translateY(-3px)" }}
                onKeyDown={() => {}}
                onClick={() => {
                  window.open(`mailto:${quick_request_created_by_email}`);
                }}
              >
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 0 }}
                  overlay={<Tooltip id="button-tooltip">{`Mail to: ${quick_request_created_by_email}`}</Tooltip>}
                >
                  <h5 className="font-weight-normal">{quick_request_created_by_name}</h5>
                </OverlayTrigger>
              </div>
              {related_request_id && (
                <Link to={shipmentRedirectURL}>
                  <CalculatorIcon className="qr-icon" />
                  {related_request_id}
                </Link>
              )}
            </Col>

            <Col className="col-12 mb-3" md>
              {header(
                intl.formatMessage({
                  id: "general.status",
                  defaultMessage: "Status",
                }),
              )}
              <div className="">
                <div className="d-flex align-items-center">
                  <Badge label={quick_request_status} type={QuickRequestStatusColors[quick_request_status]} />
                </div>
              </div>
            </Col>
            <Col className="col-12 mb-3" md>
              {header(
                intl.formatMessage({
                  id: "quick-request-detail.org.dateCreated",
                  defaultMessage: "Date Created",
                }),
              )}
              <div className="d-flex">{quick_request_creation_date}</div>
            </Col>
            <Col className="col-12 mb-3" md>
              {header(
                intl.formatMessage({
                  id: "new-request.schedule.cargoPickup",
                  defaultMessage: "Pickup",
                }),
              )}
              <div
              >
                {Object.keys(address_v2 ?? {}).length > 0 ? (
                  renderPickupAddress()
                ) : (
                  <CopyToClipboard
                    copyOnly
                    text={`${pickup_address_name ? `${pickup_address_name}, ` : ""}${
                      pickup_zipcode ? `${pickup_zipcode}, ` : ""
                    }${pickup_city ? `${pickup_city}, ` : ""}${pickup_country_code ? `${pickup_country_code}` : ""}`}
                  >
                    <p className="font-b2">{pickup_address_name}</p>

                    <p className="font-b2 mt-1">
                      {pickup_state_code || ""}
                      {pickup_zipcode}, {pickup_city}, {pickup_country_code}
                    </p>
                    {pickup_end_date && pickup_end_time ? (
                      <p className="font-b2 mt-1">
                        {`${formatDate(`${pickup_date} ${pickup_time}`, DateTimeFormat.fullDateTimeFormat)} till ${
                          pickup_end_date !== pickup_date
                            ? formatDate(`${pickup_end_date} ${pickup_end_time}`, DateTimeFormat.fullDateTimeFormat)
                            : formatDate(parseDate(pickup_end_time, "HH:mm:ss"), DateTimeFormat.timeFormat)
                        }`}
                      </p>
                    ) : (
                      <p className="font-b2 mt-1">
                        {formatDate(`${pickup_date} ${pickup_time}`, DateTimeFormat.fullDateTimeFormat)}
                      </p>
                    )}
                  </CopyToClipboard>
                )}
              </div>
            </Col>
            <Col className="col-12 mb-3" md>
              {header(
                intl.formatMessage({
                  id: "new-request.schedule.cargoDelivery",
                  defaultMessage: "Delivery",
                }),
              )}
              <div
              >
                {Object.keys(address_v2 ?? {}).length > 0 ? (
                  renderDeliveryAddress()
                ) : (
                  <CopyToClipboard
                    copyOnly
                    text={`${stop1_address_name ? `${stop1_address_name}, ` : ""}${
                      stop1_zipcode ? `${stop1_zipcode}, ` : ""
                    }${stop1_city ? `${stop1_city}, ` : ""}${stop1_country_code ? `${stop1_country_code}` : ""}`}
                  >
                    <p className="font-b2">{stop1_address_name}</p>
                    <p className="font-b2 mt-1">
                      {stop1_state_code || ""}
                      {stop1_zipcode}, {stop1_city}, {stop1_country_code}
                    </p>
                    {stop1_datetime === "ASAP" && <p className="font-b2 mt-1">ASAP Delivery</p>}
                    {stop1_datetime !== "ASAP" &&
                      (stop1_end_date && stop1_end_time ? (
                        <p className="font-b2 mt-1">
                          {`${formatDate(`${stop1_date} ${stop1_time}`, DateTimeFormat.fullDateTimeFormat)} till ${
                            stop1_end_date !== stop1_date
                              ? formatDate(`${stop1_end_date} ${stop1_end_time}`, DateTimeFormat.fullDateTimeFormat)
                              : formatDate(parseDate(stop1_end_time, "HH:mm:ss"), DateTimeFormat.timeFormat)
                          }
                     `}
                        </p>
                      ) : (
                        <p className="font-b2 mt-1">
                          {formatDate(`${stop1_date} ${stop1_time}`, DateTimeFormat.fullDateTimeFormat)}
                        </p>
                      ))}
                  </CopyToClipboard>
                )}
              </div>
            </Col>
          </Row>
        </CardNew.Body>
      </CardNew>
    </Row>
  );
}

QuickQuoteInfo.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  loadingData: PropTypes.bool.isRequired,
};

export default QuickQuoteInfo;
